import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useFirm, FirmMark } from '@/lib/brand';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Languages, Loader2, ScrollText, Scale, ShieldCheck, Fingerprint } from 'lucide-react';
import { isPasskeySupported } from '@/lib/webauthnClient';

export default function Login() {
  const { login, verifyMfa, loginWithPasskey, user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { firm } = useFirm();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('Admin123!');
  const [busy, setBusy] = useState(false);
  const [publicFirm, setPublicFirm] = useState(null);
  // MFA challenge state
  const [mfaChallenge, setMfaChallenge] = useState(null); // { challenge_token }
  const [mfaCode, setMfaCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const passkeySupported = isPasskeySupported();

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);
  useEffect(() => { api.get('/settings/public').then(r => setPublicFirm(r.data)).catch(() => {}); }, []);

  const displayFirm = firm || publicFirm;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email.trim().toLowerCase(), password);
      if (res?.mfa_required) {
        setMfaChallenge({ challenge_token: res.challenge_token });
        toast.info(lang === 'es' ? 'Se requiere código de verificación' : 'Verification code required');
        return;
      }
      toast.success(lang === 'es' ? 'Bienvenido' : 'Welcome');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('login_error'));
    } finally { setBusy(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (!mfaChallenge?.challenge_token || !mfaCode.trim()) return;
    setBusy(true);
    try {
      await verifyMfa(mfaChallenge.challenge_token, mfaCode.trim(), useBackup);
      toast.success(lang === 'es' ? 'Verificación exitosa' : 'Verification successful');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'es' ? 'Código inválido' : 'Invalid code'));
    } finally { setBusy(false); }
  };

  const cancelMfa = () => {
    setMfaChallenge(null);
    setMfaCode('');
    setUseBackup(false);
  };

  const submitPasskey = async () => {
    setBusy(true);
    try {
      await loginWithPasskey(email.trim().toLowerCase());
      toast.success(lang === 'es' ? 'Bienvenido' : 'Welcome');
      navigate('/');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || (lang === 'es' ? 'No se pudo autenticar con Passkey' : 'Passkey authentication failed');
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative overflow-hidden text-white firm-navy-hero">
        {/* Subtle noise / dot pattern */}
        <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='%23c9a961' viewBox='0 0 40 40'><path d='M0 0h1v1H0zM20 20h1v1h-1z'/></svg>")` }} />
        {/* Gold decorative rules */}
        <div className="absolute top-24 left-12 w-16 h-[2px] bg-firm-gold opacity-70" />
        <div className="absolute bottom-24 right-12 w-16 h-[2px] bg-firm-gold opacity-70" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <FirmMark size={56} variant="onDark" />
            <div>
              <div className="font-display text-2xl font-semibold tracking-tight">{displayFirm?.firm_name || 'EZ DockIT'}</div>
              <div className="text-[11px] text-firm-gold tracking-[0.28em] uppercase mt-0.5">Practice Management</div>
            </div>
          </div>

          <div>
            {/* Big centered logo mark */}
            <div className="flex justify-center mb-10">
              <div className="p-6 rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/10">
                <img
                  src="/brand/erg-logo.webp"
                  alt="Brand mark"
                  className="w-56 h-56 object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <span className="gold-rule" />
              <div className="text-[11px] uppercase tracking-[0.28em] text-firm-gold flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5" /> Para bufetes en Puerto Rico
              </div>
            </div>
            <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight">
              Cobre y emita recibos<br />profesionales<br /><em className="not-italic text-firm-gold">en menos de 1 minuto</em>.
            </h1>
            <p className="mt-8 text-sm text-white/70 max-w-md leading-relaxed">
              Stripe, ATH Móvil y métodos manuales. Numeración secuencial,
              PDF profesional, auditoría y reportes de reconciliación — todo en una
              interfaz elegante y confiable.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                { icon: ScrollText, label: 'Recibos con PDF' },
                { icon: Scale, label: 'Auditoría legal' },
                { icon: ShieldCheck, label: 'Seguro por diseño' },
              ].map((f, i) => (
                <div key={i} className="border border-white/10 rounded-lg p-3 bg-white/[0.03] hover:border-firm-gold/40 transition-colors">
                  <f.icon className="h-4 w-4 text-firm-gold mb-2" />
                  <div className="text-[11px] text-white/80 leading-tight">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-white/40 flex items-center gap-2">
            <span className="h-[1px] w-6 bg-firm-gold/60" />
            © {new Date().getFullYear()} {displayFirm?.firm_name || 'EZ DockIT'} · San Juan, PR
          </div>
        </div>
      </div>

      {/* Right login card */}
      <div className="flex items-center justify-center px-6 py-10 lg:py-0 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden flex items-center gap-2.5">
              <FirmMark size={36} />
              <span className="font-display font-semibold text-slate-900">{displayFirm?.firm_name || 'EZ DockIT'}</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="language-toggle" className="ml-auto gap-1">
                  <Languages className="h-4 w-4" />
                  <span className="uppercase text-xs font-medium">{lang}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLang('es')} data-testid="lang-es">Español</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang('en')} data-testid="lang-en">English</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <Card className="border border-slate-200 shadow-none">
            <CardContent className="p-6 sm:p-8">
              {!mfaChallenge && (
                <>
                  <h2 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">{t('login_title')}</h2>
                  <p className="text-sm text-slate-500 mt-2">{t('login_subtitle')}</p>
                  <form onSubmit={submit} className="mt-7 space-y-4">
                    <div>
                      <Label htmlFor="email" className="text-slate-700 text-xs uppercase tracking-wide">{t('email')}</Label>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="login-email-input"
                        className="mt-1.5 h-11"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label htmlFor="password" className="text-slate-700 text-xs uppercase tracking-wide">{t('password')}</Label>
                        <Link to="/olvide-password" className="text-xs text-slate-600 hover:text-slate-900 underline" data-testid="forgot-password-link">
                          ¿Olvidó su contraseña?
                        </Link>
                      </div>
                      <PasswordInput
                        id="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="login-password-input"
                        className="h-11"
                      />
                    </div>
                    <Button type="submit" disabled={busy} className="w-full h-11 bg-firm-navy hover:bg-firm-navy-strong text-white shadow-md shadow-firm-navy/10 focus-visible:ring-2 focus-visible:ring-firm-gold focus-visible:ring-offset-2" data-testid="login-submit-button">
                      {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {t('login_button')}
                    </Button>
                    <p className="text-xs text-center text-slate-500 pt-2">
                      ¿Nueva firma? <Link to="/register" className="underline text-slate-900" data-testid="register-link">Regístrese aquí</Link> · 14 días gratis
                    </p>
                  </form>
                  {passkeySupported && (
                    <>
                      <div className="my-5 flex items-center gap-3">
                        <div className="h-px bg-slate-200 flex-1" />
                        <span className="text-[11px] uppercase tracking-widest text-slate-400">
                          {lang === 'es' ? 'o' : 'or'}
                        </span>
                        <div className="h-px bg-slate-200 flex-1" />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={submitPasskey}
                        disabled={busy}
                        className="w-full h-11 gap-2"
                        data-testid="login-passkey-button"
                      >
                        <Fingerprint className="h-4 w-4" />
                        {lang === 'es' ? 'Entrar con Passkey' : 'Sign in with Passkey'}
                      </Button>
                      <p className="mt-2 text-[11px] text-slate-500 text-center" data-testid="login-passkey-hint">
                        {lang === 'es'
                          ? 'Use Face ID, Touch ID o Windows Hello si ya registró un dispositivo.'
                          : 'Use Face ID, Touch ID, or Windows Hello if you have registered a device.'}
                      </p>
                    </>
                  )}
                </>
              )}

              {mfaChallenge && (
                <>
                  <h2 className="font-display text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-slate-700" />
                    {lang === 'es' ? 'Verificación en dos pasos' : 'Two-factor verification'}
                  </h2>
                  <p className="text-sm text-slate-500 mt-2" data-testid="mfa-help-text">
                    {useBackup
                      ? (lang === 'es' ? 'Ingrese un código de respaldo (formato xxxx-xxxx).' : 'Enter a backup code (xxxx-xxxx format).')
                      : (lang === 'es' ? 'Ingrese el código de 6 dígitos de su app autenticadora.' : 'Enter the 6-digit code from your authenticator app.')}
                  </p>
                  <form onSubmit={submitMfa} className="mt-6 space-y-4" data-testid="mfa-challenge-form">
                    <div>
                      <Label htmlFor="mfa-code" className="text-slate-700 text-xs uppercase tracking-wide">
                        {useBackup ? (lang === 'es' ? 'Código de respaldo' : 'Backup code') : (lang === 'es' ? 'Código' : 'Code')}
                      </Label>
                      <Input
                        id="mfa-code"
                        type="text"
                        inputMode={useBackup ? 'text' : 'numeric'}
                        autoComplete="one-time-code"
                        required
                        maxLength={useBackup ? 12 : 6}
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        placeholder={useBackup ? 'xxxx-xxxx' : '000000'}
                        data-testid="mfa-code-input"
                        className="mt-1.5 h-11 tracking-widest text-center text-lg font-mono"
                        autoFocus
                      />
                    </div>
                    <Button type="submit" disabled={busy} className="w-full h-11" data-testid="mfa-verify-button" style={{ backgroundColor: 'var(--brand-primary, #0f172a)' }}>
                      {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {lang === 'es' ? 'Verificar' : 'Verify'}
                    </Button>
                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        className="text-slate-600 hover:text-slate-900 underline"
                        onClick={() => { setUseBackup(v => !v); setMfaCode(''); }}
                        data-testid="mfa-toggle-backup"
                      >
                        {useBackup
                          ? (lang === 'es' ? 'Usar código de la app' : 'Use code from app')
                          : (lang === 'es' ? 'Usar código de respaldo' : 'Use backup code')}
                      </button>
                      <button
                        type="button"
                        className="text-slate-500 hover:text-slate-800 underline"
                        onClick={cancelMfa}
                        data-testid="mfa-cancel-button"
                      >
                        {lang === 'es' ? 'Cancelar' : 'Cancel'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
          <div className="mt-4 text-center text-[11px] text-slate-400 space-x-3">
            <Link to="/legal/tos" className="hover:text-slate-700" data-testid="login-tos-link">Términos</Link>
            <span>·</span>
            <Link to="/legal/privacidad" className="hover:text-slate-700" data-testid="login-privacy-link">Privacidad</Link>
            <span>·</span>
            <Link to="/legal/cookies" className="hover:text-slate-700" data-testid="login-cookies-link">Cookies</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
